import { AxiosProgressEvent } from 'axios'
import { clientInstance } from '../utils/http-client'
import {
  Document,
  DocumentTag,
  DocumentListResponse,
  DocumentFilters,
  DocumentContainerQuota,
} from '../types/documents'

export interface UploadDocumentParams {
  file: File
  name?: string
  tags?: string[]
  product_id?: number
  variant_id?: number
  contact_company_id?: number
}

export interface UpdateDocumentParams {
  name?: string
  tags?: string[]
  product_id?: number | null
  variant_id?: number | null
  contact_company_id?: number | null
}

export interface ReplaceDocumentParams {
  file: File
  name?: string
}

/**
 * Upload a document with optional metadata.
 *
 * @param params - Upload parameters
 * @param onProgress - Progress callback (0-100)
 * @returns Promise<Document>
 * @throws Error with validation messages
 */
export const uploadDocument = async (
  params: UploadDocumentParams,
  onProgress?: (progress: number) => void
): Promise<Document> => {
  const formData = new FormData()
  formData.append('file', params.file)

  if (params.name) {
    formData.append('name', params.name)
  }

  if (params.tags && params.tags.length > 0) {
    formData.append('tags', JSON.stringify(params.tags))
  }

  if (params.product_id) {
    formData.append('product_id', params.product_id.toString())
  }

  if (params.variant_id) {
    formData.append('variant_id', params.variant_id.toString())
  }

  if (params.contact_company_id) {
    formData.append('contact_company_id', params.contact_company_id.toString())
  }

  try {
    const response = await clientInstance.post<Document>(
      '/documents/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            onProgress(percentCompleted)
          }
        },
      }
    )

    return response.data
  } catch (error: any) {
    // Transform API errors to user-friendly messages
    if (error.response?.data) {
      const errors = error.response.data

      if (typeof errors === 'object') {
        // Extract first error message
        const firstError = Object.values(errors)[0]

        // Handle array of errors
        if (Array.isArray(firstError)) {
          const errorItem = firstError[0]
          // Handle ErrorDetail objects from DRF
          if (
            typeof errorItem === 'object' &&
            errorItem !== null &&
            'string' in errorItem &&
            typeof errorItem.string === 'string'
          ) {
            throw new Error(errorItem.string)
          }
          throw new Error(String(errorItem))
        }

        // Handle single error (could be ErrorDetail object or string)
        if (
          typeof firstError === 'object' &&
          firstError !== null &&
          'string' in firstError &&
          typeof firstError.string === 'string'
        ) {
          throw new Error(firstError.string)
        }

        // Handle plain string errors
        if (typeof firstError === 'string') {
          throw new Error(firstError)
        }

        throw new Error(String(firstError))
      }
    }
    throw new Error(error.message || 'Upload failed')
  }
}

/**
 * Get paginated list of documents with filtering, sorting, and pagination.
 *
 * @param filters - Filter, sort, and pagination parameters
 * @returns Promise<DocumentListResponse>
 */
export const getDocuments = async (
  filters: DocumentFilters = {}
): Promise<DocumentListResponse> => {
  // Build query params from filters
  const params: Record<string, any> = {}

  if (filters.search) params.search = filters.search
  if (filters.tags) params.tags = filters.tags
  if (filters.product) params.product = filters.product
  if (filters.variant) params.variant = filters.variant
  if (filters.contact_company) params.contact_company = filters.contact_company
  if (filters.created_by) params.created_by = filters.created_by
  if (filters.created_greater) params.created_greater = filters.created_greater
  if (filters.created_less) params.created_less = filters.created_less
  if (filters.modified_greater)
    params.modified_greater = filters.modified_greater
  if (filters.modified_less) params.modified_less = filters.modified_less
  if (filters.tab) params.tab = filters.tab
  if (filters.type) params.type = filters.type
  if (filters.ordering) params.ordering = filters.ordering
  if (filters.page) params.page = filters.page
  if (filters.page_size) params.page_size = filters.page_size
  if (filters.filter_id) params.filter_id = filters.filter_id
  if (filters.is_image !== undefined) params.is_image = filters.is_image

  const response = await clientInstance.get<DocumentListResponse>(
    '/documents/',
    { params }
  )
  return response.data
}

/**
 * Get storage quota information for current user's company.
 *
 * @returns Promise<DocumentContainerQuota>
 */
export const getStorageQuota = async (): Promise<DocumentContainerQuota> => {
  const response =
    await clientInstance.get<DocumentContainerQuota>('/documents/quota/')
  return response.data
}

/**
 * Search document tags for autocomplete.
 *
 * @param search - Search query
 * @returns Promise<DocumentTag[]>
 */
export const searchDocumentTags = async (
  search: string
): Promise<DocumentTag[]> => {
  const response = await clientInstance.get<DocumentTag[]>('/documents/tags/', {
    params: { search },
  })
  return response.data
}

/**
 * Update a document's metadata.
 *
 * @param documentId - ID of the document to update
 * @param params - Update parameters (only provided fields will be updated)
 * @returns Promise<Document>
 * @throws Error with validation messages
 */
export const updateDocument = async (
  documentId: number,
  params: UpdateDocumentParams
): Promise<Document> => {
  // Build request body with only provided fields
  const data: Record<string, any> = {}

  if (params.name !== undefined) {
    data.name = params.name
  }

  if (params.tags !== undefined) {
    data.tags = JSON.stringify(params.tags)
  }

  if (params.product_id !== undefined) {
    data.product_id = params.product_id
  }

  if (params.variant_id !== undefined) {
    data.variant_id = params.variant_id
  }

  if (params.contact_company_id !== undefined) {
    data.contact_company_id = params.contact_company_id
  }

  try {
    const response = await clientInstance.patch<Document>(
      `/documents/${documentId}/`,
      data
    )
    return response.data
  } catch (error: any) {
    // Transform API errors to user-friendly messages
    if (error.response?.data) {
      const errors = error.response.data

      // Handle DRF detail format
      if (errors.detail) {
        throw new Error(errors.detail)
      }

      if (typeof errors === 'object') {
        // Extract first error message
        const firstError = Object.values(errors)[0]

        // Handle array of errors
        if (Array.isArray(firstError)) {
          const errorItem = firstError[0]
          // Handle ErrorDetail objects from DRF
          if (
            typeof errorItem === 'object' &&
            errorItem !== null &&
            'string' in errorItem &&
            typeof errorItem.string === 'string'
          ) {
            throw new Error(errorItem.string)
          }
          throw new Error(String(errorItem))
        }

        // Handle single error (could be ErrorDetail object or string)
        if (
          typeof firstError === 'object' &&
          firstError !== null &&
          'string' in firstError &&
          typeof firstError.string === 'string'
        ) {
          throw new Error(firstError.string)
        }

        // Handle plain string errors
        if (typeof firstError === 'string') {
          throw new Error(firstError)
        }

        throw new Error(String(firstError))
      }
    }
    throw new Error(error.message || 'Update failed')
  }
}

/**
 * Replace a document with a new file version.
 *
 * @param documentId - ID of the document to replace
 * @param params - Replace parameters
 * @param onProgress - Progress callback (0-100)
 * @returns Promise<Document>
 * @throws Error with validation messages
 */
export const replaceDocument = async (
  documentId: number,
  params: ReplaceDocumentParams,
  onProgress?: (progress: number) => void
): Promise<Document> => {
  const formData = new FormData()
  formData.append('file', params.file)

  if (params.name) {
    formData.append('name', params.name)
  }

  try {
    const response = await clientInstance.post<Document>(
      `/documents/${documentId}/replace/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            onProgress(percentCompleted)
          }
        },
      }
    )

    return response.data
  } catch (error: any) {
    // Transform API errors to user-friendly messages (same as uploadDocument)
    if (error.response?.data) {
      const errors = error.response.data

      // Handle DRF detail format
      if (errors.detail) {
        throw new Error(errors.detail)
      }

      if (typeof errors === 'object') {
        const firstError = Object.values(errors)[0]
        if (Array.isArray(firstError)) {
          const errorItem = firstError[0]
          if (
            typeof errorItem === 'object' &&
            errorItem !== null &&
            'string' in errorItem &&
            typeof errorItem.string === 'string'
          ) {
            throw new Error(errorItem.string)
          }
          throw new Error(String(errorItem))
        }

        if (
          typeof firstError === 'object' &&
          firstError !== null &&
          'string' in firstError &&
          typeof firstError.string === 'string'
        ) {
          throw new Error(firstError.string)
        }

        if (typeof firstError === 'string') {
          throw new Error(firstError)
        }

        throw new Error(String(firstError))
      }
    }
    throw new Error(error.message || 'Replace failed')
  }
}

/**
 * Delete a document by ID.
 *
 * @param documentId - ID of the document to delete
 * @returns Promise<void>
 * @throws Error if deletion fails
 */
export const deleteDocument = async (documentId: number): Promise<void> => {
  await clientInstance.delete(`/documents/${documentId}/`)
}
